import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  RectangleStackIcon,
  ArrowRightIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  BanknotesIcon,
  FolderIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/20/solid";
import { useVenue } from "./VenueProvider";
import { Heading } from "./heading";
import MetodosPagoManager from "./MetodosPagoManager";
import CotizacionSettings from "./CotizacionSettings";
import ArchivosTiposManager from "./ArchivosTiposManager";
import TelegramManager from "./TelegramManager";
import VenuePage from "./VenuePage";

const ACCENT = "#1b4d2e";

// Configuración por pestañas. Antes era una sola columna con cuatro
// administradores apilados: había que scrollear media pantalla para llegar a
// los tipos de archivo, y nada decía qué más había abajo.
//
// La ficha del venue vive aquí (pestaña Venue) y ya no en el menú lateral:
// para un owner es configuración —se edita de vez en cuando—, no una sección
// a la que entra todos los días. Los admins sí conservan la lista de venues
// en su sección, que es otra cosa: la vista multi-venue.
const TABS = [
  { id: "venue", label: "Venue", Icon: BuildingOfficeIcon },
  { id: "cotizacion", label: "Cotización", Icon: DocumentTextIcon },
  { id: "pagos", label: "Métodos de pago", Icon: BanknotesIcon },
  { id: "archivos", label: "Archivos", Icon: FolderIcon },
  { id: "telegram", label: "Telegram", Icon: PaperAirplaneIcon, flag: "telegram" },
];

// El catálogo se mudó a Servicios, pero este sigue siendo el lugar donde el
// owner lo busca.
const AvisoServicios = () => (
  <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-5 shadow-sm">
    <div className="flex flex-wrap items-center gap-3">
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
        style={{ backgroundColor: ACCENT + "1a" }}
      >
        <RectangleStackIcon className="h-5 w-5" style={{ color: ACCENT }} />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-zinc-900 dark:text-white">
          Los conceptos se mudaron a Servicios
        </div>
        <p className="text-sm text-zinc-500 mt-0.5">
          Es tu catálogo: cada servicio sabe lo que cobras, quién lo da y si
          tiene piezas físicas. Tus conceptos actuales siguen ahí, tal cual.
        </p>
      </div>
      <Link
        to="/dashboard/servicios"
        className="ml-auto shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
        style={{ backgroundColor: ACCENT }}
      >
        Ir a Servicios
        <ArrowRightIcon className="h-4 w-4" />
      </Link>
    </div>
  </div>
);

const Configuracion = () => {
  const { selectedVenueId, selectedVenue, venueFeatures } = useVenue();
  const [searchParams, setSearchParams] = useSearchParams();

  // Telegram solo existe para los venues con el flag beta: una pestaña que
  // no pinta nada es peor que no tenerla.
  const tabs = TABS.filter((t) => !t.flag || venueFeatures?.[t.flag] === true);

  const pedido = searchParams.get("tab");
  const activa = tabs.some((t) => t.id === pedido) ? pedido : tabs[0].id;

  const elegir = (id) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", id);
      return next;
    }, { replace: true });

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-5">
        <div className="flex items-center gap-3">
          <Heading level={2}>Configuración</Heading>
          {selectedVenue?.name && (
            <span className="text-zinc-400 hidden sm:inline">
              – {selectedVenue.name}
            </span>
          )}
        </div>
      </div>

      {/* Misma barra que Cuentas: se envuelve en dos filas en móvil en vez
          de scrollear a lo ancho. */}
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm overflow-hidden mb-6">
        <div className="flex flex-wrap border-b border-zinc-200 dark:border-zinc-700/60">
          {tabs.map((t) => {
            const esActiva = t.id === activa;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => elegir(t.id)}
                aria-current={esActiva ? "page" : undefined}
                className={`basis-1/3 grow sm:basis-0 sm:flex-1 min-w-0 px-2 py-3 text-sm font-semibold leading-tight text-center transition border-b-2 ${
                  esActiva
                    ? "text-zinc-900 dark:text-white"
                    : "text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 border-transparent"
                }`}
                style={esActiva ? { borderColor: ACCENT } : undefined}
              >
                <t.Icon
                  className={`h-4 w-4 mx-auto mb-0.5 ${esActiva ? "" : "text-zinc-400"}`}
                  style={esActiva ? { color: ACCENT } : undefined}
                />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {activa === "venue" && selectedVenueId && (
        <VenuePage venueId={selectedVenueId} embebido />
      )}
      {activa === "cotizacion" && (
        <div className="space-y-6">
          <AvisoServicios />
          <CotizacionSettings />
        </div>
      )}
      {activa === "pagos" && <MetodosPagoManager />}
      {activa === "archivos" && <ArchivosTiposManager />}
      {activa === "telegram" && <TelegramManager />}
    </div>
  );
};

export default Configuracion;
